/**
 * useUseCaseDetail — resolve a Use Case by its solution_map.id (the stable, same-name
 * -safe PK the UC detail route carries) into { industry, name, tag }.
 *
 * GetUcFull() returns industries[].areas[].ucs[] (each UcNode has solutionMapId /
 * name / tag). We load ALL industries (the route only has the PK, not the industry)
 * and find the node by solutionMapId — name alone is ambiguous across sales areas, so
 * the PK match is deliberate (see UcNode docstring).
 *
 * The industry that CONTAINS the node is captured for DISPLAY only (the page renders it
 * next to the UC name). It used to be part of the tag write's key as well, until that
 * moved to UpsertUcTagById; nothing addresses this UC by name any more.
 */
import { useCallback, useEffect, useState } from 'react';
import { getIvtClient, GetUcFullCommand } from '../../../client/IvtHubClient';

export interface UseCaseDetail {
  solutionMapId: number;
  industry: string;
  name: string;
  tag: string; // '' when no tag set
}

export interface UseUseCaseDetail {
  detail: UseCaseDetail | null;
  loading: boolean;
  error: string | null;
  /** Not-found (valid fetch, but no UC has this id) vs error (fetch failed). */
  notFound: boolean;
  reload: () => void;
}

export function useUseCaseDetail(solutionMapId: number | null): UseUseCaseDetail {
  const [detail, setDetail] = useState<UseCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (solutionMapId == null) {
      // Clear any prior result — a valid→null param transition (e.g. navigating to a
      // malformed /ivt/use-cases/abc) re-runs this effect WITHOUT remounting the page,
      // so stale `detail`/`error` would otherwise render alongside the not-found state.
      setDetail(null);
      setError(null);
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    (async () => {
      try {
        const out = await getIvtClient().send(new GetUcFullCommand({}));
        if (cancelled) return;
        // Find the UC node by PK, remembering which industry contains it.
        let found: UseCaseDetail | null = null;
        for (const ind of out.industries ?? []) {
          for (const area of ind.areas ?? []) {
            const uc = (area.ucs ?? []).find((u) => u.solutionMapId === solutionMapId);
            if (uc && ind.industry) {
              found = {
                solutionMapId,
                industry: ind.industry,
                name: uc.name ?? '',
                tag: uc.tag ?? '',
              };
              break;
            }
          }
          if (found) break;
        }
        setDetail(found);
        setNotFound(found === null);
      } catch (e) {
        if (!cancelled) setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [solutionMapId, reloadKey]);

  return { detail, loading, error, notFound, reload };
}
